import { useState } from "react";
import { History, ChevronDown, ChevronRight } from "lucide-react";
import { useDraftHistory } from "../hooks/useTimesheets";
import type { DraftChangeField, DraftHistoryEntry } from "../types";

interface Props {
  year: number;
  month: number;
  secondaryContractId: string | null;
  /** Pass when previewing someone else's history (reviewer view).
   *  Omit / null for the employee viewing their own draft. */
  employeeId?: string | null;
  /** Optional override label rendered in the header next to the icon. */
  title?: string;
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch {
    return iso;
  }
}

function formatValue(field: DraftChangeField, value: unknown): string {
  if (value === null || value === undefined || value === "") return "(brak)";
  if (field === "ranges" && Array.isArray(value)) {
    if (value.length === 0) return "(brak)";
    return (value as { from: string; to: string }[])
      .map((r) => `${r.from}–${r.to}`)
      .join(", ");
  }
  if (field === "default_shift" && typeof value === "number") {
    return `${value}:00–${value + 8}:00`;
  }
  if (field === "shift" && (typeof value === "number" || typeof value === "string")) {
    return `${value}:00 (start)`;
  }
  if (typeof value === "string" || typeof value === "number") return String(value);
  return JSON.stringify(value);
}

function dayLabel(entry: DraftHistoryEntry): string {
  if (entry.day === null || entry.day === undefined) return "okres";
  return `dzień ${entry.day}`;
}

export function DraftHistoryTimeline({
  year,
  month,
  secondaryContractId,
  employeeId,
  title,
}: Props) {
  const [open, setOpen] = useState(false);
  const { data: entries = [], isLoading } = useDraftHistory(
    year,
    month,
    secondaryContractId,
    employeeId ?? null,
    open, // lazy: query only fires once the accordion is opened
  );

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-md">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left bg-gray-50 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-md"
      >
        {open ? (
          <ChevronDown size={14} className="text-gray-400" />
        ) : (
          <ChevronRight size={14} className="text-gray-400" />
        )}
        <History size={14} className="text-gray-500" />
        <span className="font-medium text-gray-700 dark:text-gray-200">
          {title ?? "Historia zmian szkicu"}
        </span>
        {open && !isLoading && (
          <span className="text-xs text-gray-400 ml-auto">
            {entries.length}{" "}
            {entries.length === 1
              ? "zapis"
              : entries.length % 10 >= 2 &&
                  entries.length % 10 <= 4 &&
                  (entries.length % 100 < 10 || entries.length % 100 >= 20)
                ? "zapisy"
                : "zapisów"}
          </span>
        )}
      </button>

      {open && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-3 max-h-[300px] overflow-y-auto">
          {isLoading ? (
            <p className="text-xs text-gray-400">Wczytywanie…</p>
          ) : entries.length === 0 ? (
            <p className="text-xs text-gray-400">
              Brak zapisanych zmian dla tego okresu.
            </p>
          ) : (
            <ul className="space-y-2">
              {entries.map((e) => (
                <li
                  key={e.id}
                  className="text-xs text-gray-700 dark:text-gray-300 flex flex-col gap-0.5 pb-2 border-b border-gray-100 dark:border-gray-700/50 last:border-0 last:pb-0"
                >
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-mono text-gray-500 dark:text-gray-400">
                      {formatTimestamp(e.changed_at)}
                    </span>
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {dayLabel(e)}
                    </span>
                    <span className="text-gray-400">·</span>
                    <span className="font-medium text-gray-700 dark:text-gray-200">
                      {e.field_display}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-1.5 flex-wrap pl-1">
                    <span className="text-gray-400">z</span>
                    <span className="font-mono text-red-600 dark:text-red-400 line-through">
                      {formatValue(e.field, e.old_value)}
                    </span>
                    <span className="text-gray-400">→</span>
                    <span className="font-mono text-green-700 dark:text-green-400">
                      {formatValue(e.field, e.new_value)}
                    </span>
                    {e.changed_by_name && (
                      <span className="text-gray-400 ml-1">
                        · {e.changed_by_name}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
